import {
  AnswersValues,
  BonusQuestionAction,
  BonusQuestionStatus,
  BonusQuestionType,
  DiffusionValues,
  QuestionBonusChampionshipId,
  QuestionBonusVisibilityHours,
  QuizOptionValues,
  QuestionTitleValues,
  SelectOption,
  TranslationLanguage,
} from './types';

export const QUESTION_BONUS_LIST_PATH = '/mpp/question-bonus/list';
export const questionBonusEditPath = (bonusQuestionId: string) =>
  `/mpp/question-bonus/edit/${bonusQuestionId}`;
export const questionBonusViewPath = (bonusQuestionId: string) =>
  `/mpp/question-bonus/view/${bonusQuestionId}`;
export const questionBonusResolvePath = (bonusQuestionId: string) =>
  `/mpp/question-bonus/resolve/${bonusQuestionId}`;

/**
 * Un seul championnat est ouvert pour le moment (L1), mais la liste existe déjà
 * pour que l'UI et la payload soient prêtes à en accueillir d'autres.
 */
export const CHAMPIONSHIPS: {
  id: QuestionBonusChampionshipId;
  shortLabel: string;
  name: string;
}[] = [{id: 1, shortLabel: 'L1', name: 'Ligue 1 McDonald’s'}];

export const CHAMPIONSHIP_IDS: QuestionBonusChampionshipId[] = CHAMPIONSHIPS.map(
  championship => championship.id,
);

export const CHAMPIONSHIP_OPTIONS: SelectOption[] = CHAMPIONSHIPS.map(
  championship => ({value: championship.id, label: championship.shortLabel}),
);

/** `championshipId` vient de l'API, donc `number` et pas le type littéral. */
export const getChampionshipName = (championshipId?: number) =>
  CHAMPIONSHIPS.find(championship => championship.id === championshipId)?.name ||
  '-';

export const DEFAULT_CHAMPIONSHIP_ID: QuestionBonusChampionshipId = 1;

export const VISIBILITY_HOURS: QuestionBonusVisibilityHours[] = [72, 48, 24, 12];

export const VISIBILITY_OPTIONS: SelectOption[] = VISIBILITY_HOURS.map(
  hours => ({value: hours, label: `${hours}h avant début journée`}),
);

export const DEFAULT_VISIBILITY_HOURS: QuestionBonusVisibilityHours = 48;

export const QUESTION_TYPE_OPTIONS: {
  value: BonusQuestionType;
  label: string;
}[] = [
  {value: 'quiz', label: 'QCM - journée entière'},
  {value: 'matchForecast', label: 'Prono - match spécifique'},
];

export const QUESTION_TITLE_MIN_LENGTH = 20;
export const QUESTION_TITLE_MAX_LENGTH = 80;

export const ANSWER_LABEL_MAX_LENGTH = 50;
export const MIN_QUIZ_OPTIONS = 2;
export const MAX_QUIZ_OPTIONS = 4;
export const POINTS_MAX_LENGTH = 3;

export const INITIAL_DIFFUSION_VALUES: DiffusionValues = {
  championshipId: DEFAULT_CHAMPIONSHIP_ID,
  gameWeekNumber: undefined,
  visibilityHoursBeforeGameWeekStart: DEFAULT_VISIBILITY_HOURS,
};

export const INITIAL_QUESTION_VALUES: QuestionTitleValues = {
  fr: '',
  en: '',
  es: '',
};

let optionKeySequence = 0;

export const createQuizOption = (): QuizOptionValues => {
  optionKeySequence += 1;
  return {
    key: `option-${optionKeySequence}`,
    label: {fr: '', en: '', es: ''},
    points: '',
  };
};

export const createInitialAnswersValues = (): AnswersValues => ({
  quiz: {
    options: Array.from({length: MIN_QUIZ_OPTIONS}, () => createQuizOption()),
  },
  matchForecast: {
    championshipMatchId: undefined,
    quotations: {home: '', draw: '', away: ''},
  },
});

export const QUESTION_TYPE_LABELS: Record<BonusQuestionType, string> = {
  quiz: 'QCM',
  matchForecast: 'Prono',
};

export const BONUS_QUESTION_STATUS_LABELS: Record<BonusQuestionStatus, string> =
  {
    draft: 'Brouillon',
    scheduled: 'Programmée',
    open: 'Ouverte',
    live: 'LIVE',
    toClose: 'A clôturer',
    finished: 'Terminée',
    cancelled: 'Annulée',
  };

/**
 * Actions offertes par statut, dans l'ordre d'affichage.
 * `delete` et `cancel` déclenchent le même `PATCH /bonus-question/:id/cancel`,
 * seul le libellé change selon que la question a déjà été diffusée.
 */
export const BONUS_QUESTION_ACTIONS_BY_STATUS: Record<
  BonusQuestionStatus,
  BonusQuestionAction[]
> = {
  draft: ['edit', 'delete'],
  scheduled: ['edit', 'cancel'],
  open: ['view', 'cancel'],
  live: ['view', 'resolve'],
  toClose: ['view', 'resolve'],
  finished: ['view'],
  cancelled: ['view'],
};

export const BONUS_QUESTION_ACTION_LABELS: Record<BonusQuestionAction, string> =
  {
    view: 'Voir',
    edit: 'Modifier',
    delete: 'Supprimer',
    cancel: 'Annuler',
    resolve: 'Saisir la réponse',
  };

/** Font Awesome 4 (AdminLTE) ; `resolve` est un CTA texte, pas une icône. */
export const BONUS_QUESTION_ACTION_ICONS: Record<
  BonusQuestionAction,
  string | undefined
> = {
  view: 'fa fa-eye',
  edit: 'fa fa-pencil-square-o',
  delete: 'fa fa-trash-o',
  cancel: 'fa fa-trash-o',
  resolve: undefined,
};

/** Libellé du format tel qu'il apparaît sur l'écran de saisie de réponse. */
export const QUESTION_TYPE_SCOPE_LABELS: Record<BonusQuestionType, string> = {
  quiz: 'Journée entière',
  matchForecast: 'Match spécifique',
};

/** Ordre du sélecteur de la modale ; `flag` suit la convention `flag-icon-css` du BO. */
export const TRANSLATION_LANGUAGES: {
  value: TranslationLanguage;
  label: string;
  flag: string;
}[] = [
  {value: 'fr', label: 'Français', flag: 'fr'},
  {value: 'en', label: 'English', flag: 'gb-eng'},
  {value: 'es', label: 'Español', flag: 'es'},
];

/** Langues à traduire depuis la modale ; le FR est saisi dans le formulaire. */
export const TRANSLATED_LANGUAGES: TranslationLanguage[] = ['en', 'es'];



// WEBPACK FOOTER //
// ./src/pages/Other/MPP/QuestionBonus/constants.ts